import type { ResourceBudget, RoleBudget, PersonBudget } from '@/types/budget.types';
import { RateCardStorageService } from './rateCardStorage.service';
import { MockOrgDefaultsService } from './mockOrgDefaults.service';
import { getApiBaseUrl } from '@/lib/data-service';
import { apiFetch } from '@/lib/api-error-handler';

/**
 * Effective hourly rates for a single role, resolved from the live Rate Card.
 * Keyed (in the lookup map) by lower-cased role name.
 */
export interface LiveRoleRate {
  roleName: string;
  hourlyBillRate: number;
  hourlyCostRate: number;
}

export type LiveRoleRateMap = Map<string, LiveRoleRate>;

interface BackendRateCardEntryRates {
  roleName: string;
  effectiveBillRate: number;
  effectiveCostRate: number;
}

/**
 * Fetch the live Rate Card effective rates for a project and index them by
 * lower-cased role name. This is the SAME `effectiveBillRate`/`effectiveCostRate`
 * source used by the Rate Card view and the backend Labour cascade, so budget
 * calculations that use it stay consistent with the Labour heading totals.
 */
export async function fetchLiveRoleRates(projectId: string): Promise<LiveRoleRateMap> {
  const map: LiveRoleRateMap = new Map();
  try {
    const entries = await apiFetch<BackendRateCardEntryRates[]>(
      `${getApiBaseUrl()}/ratecardentries/by-project/${projectId}`
    );
    (Array.isArray(entries) ? entries : []).forEach(entry => {
      if (!entry?.roleName) return;
      map.set(entry.roleName.toLowerCase(), {
        roleName: entry.roleName,
        hourlyBillRate: entry.effectiveBillRate,
        hourlyCostRate: entry.effectiveCostRate,
      });
    });
  } catch (error) {
    console.warn('[BudgetCalculationService] Failed to fetch live role rates:', error);
  }
  return map;
}

export interface MonthlyCalculation {
  month: string; // YYYY-MM format
  hours: number;
  costRate: number;
  billRate: number;
  cost: number;
  revenue: number;
  margin: number;
}

export interface RoleCalculation {
  roleId: string;
  roleName: string;
  totalHours: number;
  totalCost: number;
  totalRevenue: number;
  averageCostRate: number;
  averageBillRate: number;
  margin: number;
  monthlyBreakdown: MonthlyCalculation[];
}

export interface BudgetCalculationResult {
  totalLabourCost: number;
  totalRevenue: number;
  totalMargin: number;
  marginPercentage: number;
  roleBreakdown: RoleCalculation[];
  calculatedAt: string;
}

export class BudgetCalculationService {
  /**
   * Calculate Labour cost and Revenue from Resource Budget
   */
  static calculateBudget(
    projectId: string,
    resourceBudget: ResourceBudget,
    liveRoleRates?: LiveRoleRateMap
  ): BudgetCalculationResult {
    const roleBreakdown: RoleCalculation[] = [];
    let totalLabourCost = 0;
    let totalRevenue = 0;

    // Process each role in the resource budget
    if (resourceBudget.byRole && resourceBudget.byRole.length > 0) {
      for (const roleBudget of resourceBudget.byRole) {
        const roleCalc = this.calculateRoleBudget(projectId, roleBudget, resourceBudget, liveRoleRates);
        roleBreakdown.push(roleCalc);
        totalLabourCost += roleCalc.totalCost;
        totalRevenue += roleCalc.totalRevenue;
      }
    }

    const totalMargin = totalRevenue - totalLabourCost;
    const marginPercentage = totalRevenue > 0 ? (totalMargin / totalRevenue) * 100 : 0;

    const result: BudgetCalculationResult = {
      totalLabourCost: Math.round(totalLabourCost * 100) / 100,
      totalRevenue: Math.round(totalRevenue * 100) / 100,
      totalMargin: Math.round(totalMargin * 100) / 100,
      marginPercentage: Math.round(marginPercentage * 100) / 100,
      roleBreakdown,
      calculatedAt: new Date().toISOString(),
    };

    return result;
  }

  /**
   * Calculate budget for a specific role
   */
  private static calculateRoleBudget(
    projectId: string,
    roleBudget: RoleBudget,
    resourceBudget: ResourceBudget,
    liveRoleRates?: LiveRoleRateMap
  ): RoleCalculation {
    // Resolve the effective rates for this role.
    // In live mode (liveRoleRates provided) use the live Rate Card effective
    // rates — the same source as the Rate Card view and the backend Labour
    // cascade. Otherwise (demo/tour) fall back to the mock-backed
    // RateCardStorageService hierarchy (person override > project override > org default).
    let hourlyBillRate: number | undefined;
    let hourlyCostRate: number | undefined;

    if (liveRoleRates) {
      const liveRate = liveRoleRates.get(roleBudget.role?.toLowerCase() ?? '');
      if (liveRate) {
        hourlyBillRate = liveRate.hourlyBillRate;
        hourlyCostRate = liveRate.hourlyCostRate;
      }
    } else {
      const roleRates = RateCardStorageService.getAllRoleRates(projectId);
      const roleRate = roleRates.find(r => r.roleName === roleBudget.role);
      if (roleRate) {
        hourlyBillRate = roleRate.hourlyBillRate;
        hourlyCostRate = roleRate.hourlyCostRate;
      }
    }

    if (hourlyBillRate === undefined) {
      const fallbackBillRate = roleBudget.billRate || 0;
      const fallbackCostRate = roleBudget.costRate || fallbackBillRate;
      const totalHours = roleBudget.budgetHours || 0;
      const totalCost = totalHours * fallbackCostRate;
      const totalRevenue = totalHours * fallbackBillRate;
      const margin = totalRevenue - totalCost;
      return {
        roleId: roleBudget.role,
        roleName: roleBudget.role,
        totalHours: Math.round(totalHours * 100) / 100,
        totalCost: Math.round(totalCost * 100) / 100,
        totalRevenue: Math.round(totalRevenue * 100) / 100,
        averageCostRate: fallbackCostRate,
        averageBillRate: fallbackBillRate,
        margin: Math.round(margin * 100) / 100,
        monthlyBreakdown: [],
      };
    }

    const effectiveCostRate = hourlyCostRate ?? 0;

    // SIMPLIFIED: Just use role-level hours × role-level rates
    // Person-level detail is only for display in Rate Card, not for budget calculations
    const totalHours = roleBudget.budgetHours || 0;
    const totalCost = totalHours * effectiveCostRate;
    const totalRevenue = totalHours * hourlyBillRate;
    const margin = totalRevenue - totalCost;

    return {
      roleId: roleBudget.role,
      roleName: roleBudget.role,
      totalHours: Math.round(totalHours * 100) / 100,
      totalCost: Math.round(totalCost * 100) / 100,
      totalRevenue: Math.round(totalRevenue * 100) / 100,
      averageCostRate: effectiveCostRate,
      averageBillRate: hourlyBillRate,
      margin: Math.round(margin * 100) / 100,
      monthlyBreakdown: [],
    };
  }

  /**
   * Calculate budget impact of a rate change
   */
  static calculateRateChangeImpact(
    projectId: string,
    resourceBudget: ResourceBudget,
    roleId: string,
    newCostRate: number,
    newBillRate: number,
    effectiveFrom: string
  ): {
    revenueImpact: number;
    costImpact: number;
    marginImpact: number;
    affectedMonths: string[];
    remainingHours: number;
  } {
    // Get current calculation
    const currentCalc = this.calculateBudget(projectId, resourceBudget);
    const roleCalc = currentCalc.roleBreakdown.find(r => r.roleName === roleId);

    if (!roleCalc) {
      return {
        revenueImpact: 0,
        costImpact: 0,
        marginImpact: 0,
        affectedMonths: [],
        remainingHours: 0,
      };
    }

    // Calculate impact on future months only (from effectiveFrom date)
    const effectiveDate = new Date(effectiveFrom);
    const affectedMonths: string[] = [];
    let remainingHours = 0;
    let oldCost = 0;
    let oldRevenue = 0;
    let newCost = 0;
    let newRevenue = 0;

    for (const monthCalc of roleCalc.monthlyBreakdown) {
      const monthDate = new Date(monthCalc.month + '-01');
      if (monthDate >= effectiveDate) {
        affectedMonths.push(monthCalc.month);
        remainingHours += monthCalc.hours;
        oldCost += monthCalc.cost;
        oldRevenue += monthCalc.revenue;
        newCost += monthCalc.hours * newCostRate;
        newRevenue += monthCalc.hours * newBillRate;
      }
    }

    const costImpact = newCost - oldCost;
    const revenueImpact = newRevenue - oldRevenue;
    const marginImpact = revenueImpact - costImpact;

    return {
      revenueImpact: Math.round(revenueImpact * 100) / 100,
      costImpact: Math.round(costImpact * 100) / 100,
      marginImpact: Math.round(marginImpact * 100) / 100,
      affectedMonths,
      remainingHours: Math.round(remainingHours * 100) / 100,
    };
  }
}
